import { X, FileText, FileType2, FileSpreadsheet, Palette } from 'lucide-react'
import type { CSSProperties } from 'react'
import type { Attachment } from '@/types'
import { formatBytes } from '@/lib/attachments'

interface Props {
  attachments: Attachment[]
  onRemove: (id: string) => void
  disabled?: boolean
}

// Deletable chips for prompt attachments. Image chips show a thumbnail;
// document (PDF/DOCX) and text chips show an icon + name + size. Renders
// nothing when empty, so callers can always mount it above their textarea.
export default function AttachmentChips({ attachments, onRemove, disabled }: Props) {
  if (!attachments || attachments.length === 0) return null
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 0 6px' }}>
      {attachments.map((att) => (
        <span
          key={att.id}
          style={chipStyle}
          title={
            att.kind === 'document'
              ? `${att.name} — the text is extracted server-side`
              : att.kind === 'spreadsheet'
                ? `${att.name} — every row is available to import`
                : att.kind === 'design'
                  ? `${att.name} — Claude Design, imported when you send`
                  : att.name
          }
        >
          {att.kind === 'image' && att.data_b64 ? (
            <img
              src={`data:${att.mime};base64,${att.data_b64}`}
              alt={att.name}
              style={{ width: 18, height: 18, borderRadius: 3, objectFit: 'cover', flexShrink: 0 }}
            />
          ) : att.kind === 'document' ? (
            <FileType2 size={12} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />
          ) : att.kind === 'spreadsheet' ? (
            <FileSpreadsheet size={12} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />
          ) : att.kind === 'design' ? (
            <Palette size={12} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />
          ) : (
            <FileText size={12} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />
          )}
          <span
            style={{
              maxWidth: 140,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {att.name}
          </span>
          <span style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }}>
            {formatBytes(att.size)}
          </span>
          {!disabled && (
            <button
              onClick={() => onRemove(att.id)}
              title="Remove"
              aria-label={`Remove ${att.name}`}
              style={chipRemoveStyle}
            >
              <X size={11} />
            </button>
          )}
        </span>
      ))}
    </div>
  )
}

const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 7px',
  fontSize: '0.8125rem',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-full)',
  color: 'var(--color-text)',
  maxWidth: 240,
}

const chipRemoveStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  color: 'var(--color-text-secondary)',
  flexShrink: 0,
}
